/**
 * Per-(site, step) "Not now" dismissal state. Browser-local — the next step
 * is picked by the ranker, so dismissing one just skips it until the next
 * refresh of the page. All reads/writes wrapped in try/catch because
 * localStorage is disabled in incognito + cookies-off browsers.
 *
 * Key shape: `sill.readiness.dismissed.<site_id>` → `step_id\n…`. One file
 * per site keeps the entry small and localStorage GC-friendly.
 */

const KEY_PREFIX = 'sill.readiness.dismissed.';

function key(site_id: string): string {
  return `${KEY_PREFIX}${site_id}`;
}

export function loadDismissedSteps(site_id: string): ReadonlySet<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(key(site_id));
    if (raw === null) return new Set();
    return new Set(raw.split('\n').filter((s) => s.trim().length > 0));
  } catch {
    return new Set();
  }
}

export function dismissStep(site_id: string, step_id: string): ReadonlySet<string> {
  const next = new Set(loadDismissedSteps(site_id));
  next.add(step_id);
  persist(site_id, next);
  return next;
}

/** Remove a single `step_id` from the dismissed set. Used by the
 *  "Snoozed · Undo" affordance so the row re-enters the ranker's
 *  "Your next step" rotation. */
export function undismissStep(site_id: string, step_id: string): ReadonlySet<string> {
  const next = new Set(loadDismissedSteps(site_id));
  next.delete(step_id);
  persist(site_id, next);
  return next;
}

function persist(site_id: string, set: ReadonlySet<string>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key(site_id), Array.from(set).join('\n'));
  } catch {
    // storage disabled — state only lives for this page view
  }
}

export function clearDismissedSteps(site_id: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key(site_id));
  } catch {
    // ignore
  }
}
